import bbox from "@turf/bbox";
import { notification } from "antd";
import { push } from "connected-react-router";
import { intersection, isEqual } from "lodash-es";
import { defineMessages } from "react-intl";
import { Action } from "redux";
import { all, call, put, race, select, take, takeEvery, takeLatest } from "redux-saga/effects";
import { ActionType, isActionOf } from "typesafe-actions";

import { OnlyRequiredFields } from "@mapmycustomers/shared";
import BoundaryLegendAggregationType from "@mapmycustomers/shared/enum/boundaryLegends/BoundaryLegendAggregationType";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import TerritoryLevel from "@mapmycustomers/shared/enum/TerritoryLevel";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import {
  EntityType,
  EntityTypesSupportedByMapsPage,
  MapEntity,
  Territory,
} from "@mapmycustomers/shared/types/entity";
import {
  BusinessRule,
  BusinessRuleAssignmentPayload,
  PlatformBusinessRule,
} from "@mapmycustomers/shared/types/entity/BusinessRule";
import {
  CategorizedMapEntries,
  MapEntry,
  MapRecordsResponse,
} from "@mapmycustomers/shared/types/map";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import Organization, { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";
import { OrganizationSize } from "@mapmycustomers/shared/types/OrganizationSize";
import SmartTerritoryConfig from "@mapmycustomers/shared/types/territory/SmartTerritoryConfig";
import User from "@mapmycustomers/shared/types/User";
import { MapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel, {
  PinLegendsFilter,
} from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";
import PlatformMapRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformMapRequest";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { parseAreaType } from "@mapmycustomers/shared/util/territory/areaTypes";

import { FILTER_ENTITY_TYPES } from "@app/component/filtering/utils/consts";
import getErrorNotificationDescription from "@app/component/map/core/utils/getErrorNotificationDescription";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import MapMode from "@app/enum/map/MapMode";
import Path from "@app/enum/Path";
import ReportType from "@app/enum/ReportType";
import {
  cleanLassoPaths,
  clearSelection,
  countPinsInTerritory,
  enterLassoMode,
  enterMode,
  exitMode,
  exitTerritoryLassoMode,
  fetchTerritoryLassoSelection,
  resetRecordsListPagination,
  selectMapTool,
  setDataViewData,
  setHighlight,
  setSelection,
  setSelectionEntities,
  toggleDataViewVisibility,
  updateDataViewEntityTypeCount,
  updateLassoClusters,
  updateTerritory,
  updateTerritorySharing,
  updateViewportState,
} from "@app/scene/map/store/actions";
import {
  doesTerritoryEditFormHaveChanges,
  getExcludedRecords,
  getIncludedRecords,
  getMapLassoPaths,
  getMapMode,
  getMapViewport,
  getMapViewState,
  getMapViewTool,
  getSelectedRecords,
  getTerritoryCandidate,
  getTerritoryDataViewData,
  getTerritoryEntries,
  getTerritoryId,
  getTerritoryMode,
  isTerritoryDataViewVisible,
} from "@app/scene/map/store/selectors";
import {
  applyTerritoryMapViewSettings,
  countPinsInTerritoryBusinessRule,
  downloadTerritoryRecords,
  enterTerritoryMode,
  exitTerritoryMode,
  fetchDataForTerritoryMetrics,
  fetchTerritoryBusinessRulesRecords,
  fetchTerritoryPins,
  fetchTerritoryRecords,
  initializeTerritoryMode,
  updateTerritoryBusinessRules,
} from "@app/scene/map/store/territoryMode/actions";
import {
  getSelectedBusinessRule,
  getTerritoryBusinessRules,
  getTerritoryMapViewState,
} from "@app/scene/map/store/territoryMode/selectors";
import withLassoIds from "@app/scene/map/store/territoryMode/utils/withLassoIds";
import { callApi } from "@app/store/api/callApi";
import { setTerritoryPinsBounds, setTerritoryQuery } from "@app/store/chat/viewContext/actions";
import { TerritoryRecordsQuery } from "@app/store/chat/viewContext/ViewContextState";
import { handleError } from "@app/store/errors/actions";
import { MAX_ITEMS_TO_DOWNLOAD_FILE } from "@app/store/exportEntities/const";
import {
  getCurrentUser,
  getCurrentUserId,
  getFeatures,
  getOrganizationId,
  getOrganizationSize,
} from "@app/store/iam";
import { getMapViewSettings } from "@app/store/map";
import AlertBadgesState from "@app/store/mapLayers/alerts/AlertBadgesState";
import { setHighlightedTerritoryId } from "@app/store/mapLayers/boundaries/actions";
import { getAlertsData } from "@app/store/mapLayers/selectors";
import { getAllColorLegends, getAllShapeLegends } from "@app/store/pinLegends";
import convertMapFilterModelToFilterModelForEntity from "@app/store/savedFilters/convertMapFilterModelToFilterModel";
import { getSavedFilters } from "@app/store/savedFilters/selectors";
import {
  fetchAllTerritories,
  updateTerritory as updateTerritoryGlobal,
  updateTerritorySharing as updateTerritorySharingGlobal,
} from "@app/store/territories/actions";
import GeoPath from "@app/types/GeoPath";
import MapViewportState from "@app/types/map/MapViewportState";
import TerritoryModeState from "@app/types/map/TerritoryModeState";
import Report from "@app/types/Report";
import AggregatedListResponse from "@app/types/viewModel/AggregatedListResponse";
import ListResponseAggregation from "@app/types/viewModel/ListResponseAggregation";
import getSavedFilterName from "@app/util/chat/pageContext/getSavedFilterName";
import isMapFilterApplied from "@app/util/chat/pageContext/isMapFilterApplied";
import toContextFilters from "@app/util/chat/pageContext/toContextFilters";
import toContextSort from "@app/util/chat/pageContext/toContextSort";
import { getLocalTimeZoneFormattedOffset } from "@app/util/dates";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { UniversalFieldName } from "@app/util/fieldModel/universalFieldsFieldModel";
import downloadEntitiesAsCsv from "@app/util/file/downloadEntitiesAsCsv";
import {
  createAndCondition,
  createEmptyFilterModel,
  getSimpleConditionForField,
} from "@app/util/filters/utils";
import { formatDate } from "@app/util/formatters";
import categorizeMapEntries from "@app/util/map/categorizeMapEnties";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import convertViewportToPersist from "@app/util/map/convertViewportToPersist";
import getPrecisionDetails from "@app/util/map/getPrecisionDetails";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { toCountOnlyPinsFilters, toLeanPinsRequest } from "@app/util/map/leanPinsRequest";
import getColorShapeForEntity from "@app/util/map/markerStyles/getColorShapeForEntity";
import compareFloatingPointNumbers from "@app/util/number/compareFloatingPointNumbers";
import { getEntityTypeDisplayName } from "@app/util/ui";
import { convertFromPlatformFilterModel } from "@app/util/viewModel/convertFromPlatformFilterModel";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import convertMapPlatformFilterToMapFilterModel from "@app/util/viewModel/convertMapPlatformFilterToMapFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

interface CountAggregationItem<T> extends ListResponseAggregation<T> {
  sum_adjustedAmount?: { value: number };
  sum_amount?: { value: number };
}

const messages = defineMessages({
  dataFileTooLarge: {
    id: "map.territory.recordList.exportFiles.dataFileTooLarge.warning",
    defaultMessage:
      "Data {multiple, select, true {files} other {file}} too large to download right now",
    description: "Message shown when sample companies are added",
  },
  dataFileTooLargeDescription: {
    id: "map.territory.recordList.exportFiles.dataFileTooLarge.warning.description",
    defaultMessage:
      "Your export has been queued for processing and will be sent to your email once finished.",
    description: "Message shown when sample people are added",
  },
  error: {
    id: "map.territory.recordList.exportFiles.error.message",
    defaultMessage: "Download Error",
    description: "Message shown when download fail",
  },
  success: {
    id: "map.territory.recordList.exportFiles.success",
    defaultMessage: "{multiple, select, true {Files} other {File}} Downloaded",
    description: "Message shown when sample people are added",
  },
});

const isSamePoint = ([xa, ya]: LongLat, [xb, yb]: LongLat) => {
  return compareFloatingPointNumbers(xa, xb) === 0 && compareFloatingPointNumbers(ya, yb) === 0;
};

const isClosedPath = (path: LongLat[]) => isSamePoint(path[0], path[path.length - 1]);

const hasRuleChanged = (current: BusinessRule, incoming: BusinessRule) => {
  return current.name !== incoming.name || !isEqual(current.filters, incoming.filters);
};

const hasAssignmentChanged = (current: BusinessRule, incoming: BusinessRule) => {
  return !isEqual(current.territories, incoming.territories);
};

const getTargetUserIds = (rule: BusinessRule, territoryId: Territory["id"]) =>
  rule.territories?.find((territory) => territory.territoryId === territoryId)?.userIds || [];

type ListResponseTotal = Pick<ListResponse<MapEntry>, "total">;

function* onEnterTerritoryMode({ payload }: ReturnType<typeof enterTerritoryMode>) {
  const { areaType, territoryId } = payload;

  // copy map view state when creating new territory
  if (territoryId === "create") {
    const viewState: MapViewState = yield select(getMapViewState);
    yield put(applyTerritoryMapViewSettings(viewState));
  }

  yield put(
    push(`${Path.MAP}/territories/${territoryId}${areaType ? `?areaType=${areaType}` : ""}`)
  );
}

function* onExitTerritoryMode({
  payload: forceExit,
}: ReturnType<typeof exitTerritoryMode.request>) {
  yield put(push(Path.MAP));

  const hasChanges: boolean = yield select(doesTerritoryEditFormHaveChanges);
  // only exit from territory mode immediately if there are no changes in territory or
  // if we're forced to do that
  if (!hasChanges || forceExit) {
    yield put(exitTerritoryMode.success());
    yield put(exitMode());
    yield put(setHighlightedTerritoryId(undefined));
  }
}

function* onInitializeTerritoryMode({
  payload,
}: ReturnType<typeof initializeTerritoryMode.request>) {
  const { callback, territoryId } = payload;
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield put(enterMode(MapMode.TERRITORIES));

    // initiate fetching, we don't care if it succeeds or fails
    yield put(fetchAllTerritories.request());

    if (!territoryId) {
      const currentUserId: User["id"] = yield select(getCurrentUserId);
      const platformBusinessRules: ListResponse<PlatformBusinessRule> = yield callApi(
        "fetchTerritoryBusinessRules",
        orgId,
        {
          $filters: {
            $and: [{ isSystemGenerated: true }, { createdBy: currentUserId }],
          },
          $order: "isSystemGenerated",
        }
      );

      const businessRules: BusinessRule[] = platformBusinessRules.data[0]
        ? [
            {
              ...platformBusinessRules.data[0],
              filters: {},
            },
          ]
        : [];

      yield put(initializeTerritoryMode.success({ businessRules }));
      return;
    }

    const territoryCandidate: Territory | undefined = yield select(getTerritoryCandidate);

    // fetch territory
    const territory: Territory =
      // if we have territoryCandidate with shape, use it, otherwise fetch
      territoryCandidate?.id === territoryId &&
      (territoryCandidate?.territoryDetail.type === "points" ||
        territoryCandidate?.territoryDetail.shape)
        ? territoryCandidate
        : yield callApi("fetchTerritory", orgId, territoryId);

    const platformBusinessRules: ListResponse<PlatformBusinessRule> = yield callApi(
      "fetchTerritoryBusinessRules",
      orgId,
      {
        $filters: {
          territoryIds: [territoryId],
        },
        $order: "isSystemGenerated",
      }
    );

    let bounds = new google.maps.LatLngBounds();
    if (territory.territoryDetail.type === "points") {
      territory.territoryDetail.points.coordinates.forEach((polylineCoordinates) =>
        polylineCoordinates.forEach(([lng, lat]) => {
          bounds.extend({ lat, lng });
        })
      );
    } else if (territory.territoryDetail.shape) {
      // also detect bounds, but using territory shape file
      const [minLon, minLat, maxLon, maxLat] = bbox(territory.territoryDetail.shape);
      bounds = new google.maps.LatLngBounds(
        { lat: minLat, lng: minLon },
        { lat: maxLat, lng: maxLon }
      );
    }
    callback?.(bounds);

    const businessRules: BusinessRule[] = platformBusinessRules.data.map((rule) => ({
      ...rule,
      filters: convertMapPlatformFilterToMapFilterModel(rule.filters),
    }));

    yield put(initializeTerritoryMode.success({ businessRules, territory }));
  } catch (e) {
    yield put(initializeTerritoryMode.failure());
    yield put(handleError({ error: e }));
  }
}

export function* onFetchTerritoryPins({ payload }: ReturnType<typeof fetchTerritoryPins.request>) {
  try {
    if (payload.viewport) {
      yield put(updateViewportState(payload.viewport));
      yield call(localSettings.updateViewportState, convertViewportToPersist(payload.viewport));
    }
    yield put(applyTerritoryMapViewSettings(payload.request));
    const viewport: MapViewportState = yield select(getMapViewport);
    const mapViewState: MapViewState = yield select(getTerritoryMapViewState);

    if (payload.updateOnly) {
      return;
    }

    const orgId: Organization["id"] = yield select(getOrganizationId);
    const organizationSize: OrganizationSize = yield select(getOrganizationSize);

    const selectedBusinessRule: BusinessRule | undefined = yield select(getSelectedBusinessRule);

    // If we send empty filter for some entity, backend will send all records of that entity type
    // So we need to make sure records match selected business rule entities, if there is a selected business rule
    const visibleEntities =
      selectedBusinessRule && !("universal" in selectedBusinessRule.filters)
        ? intersection(
            Object.keys(selectedBusinessRule.filters) as EntityTypesSupportedByMapsPage[],
            mapViewState.visibleEntities
          )
        : mapViewState.visibleEntities;

    // we don't need to show any pins on map, but we still needed to perform a request
    // in order to get total count
    const hideResults = visibleEntities.length === 0;

    const entityFilters: MapFilterModel = {};

    if (selectedBusinessRule) {
      FILTER_ENTITY_TYPES.forEach((entityType) => {
        const businessRuleEntityFilter = selectedBusinessRule.filters?.[entityType];
        if (businessRuleEntityFilter && businessRuleEntityFilter.conditions.length > 0) {
          entityFilters[entityType] = createAndCondition([
            businessRuleEntityFilter,
            mapViewState.filter[entityType] ?? createEmptyFilterModel(),
          ]);
        } else if (mapViewState.filter[entityType]) {
          entityFilters[entityType] = mapViewState.filter[entityType];
        }
      });
    }

    const mapFilter = convertMapFilterToPlatformFilterModel(
      selectedBusinessRule ? entityFilters : mapViewState.filter,
      visibleEntities
    );

    // we're pretty sure it exists in TerritoryMode
    const territoriesCondition = getSimpleConditionForField(
      mapViewState.filter.universal,
      UniversalFieldName.TERRITORIES
    )!;

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    const { visible: alertsVisible }: AlertBadgesState = yield select(getAlertsData);

    const requestPayload: Partial<PlatformMapRequest> = toLeanPinsRequest(
      {
        bounds: viewport.bounds,
        pinLegends: MAP_ENTITY_TYPES.reduce(
          (result, entityType) => ({
            ...result,
            [entityType]: getColorShapeForEntity(
              mapViewState,
              colorPinLegends,
              shapePinLegends,
              entityType
            ),
          }),
          { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
        ),
        ...getPrecisionDetails(viewport.zoom ?? 1, organizationSize),
        ...mapFilter,
        ...selectedBusinessRule?.filters,
      },
      { columns: ["name"], withBadges: alertsVisible }
    );

    // Territories filter is only support at top level in $and -> https://mapmycustomers.atlassian.net/browse/TART-25707
    if (territoriesCondition) {
      requestPayload.$filters!.territories = {
        $any: territoriesCondition.value,
      };
    }

    const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, requestPayload);

    let externalPinsData: MapEntry[] = [];
    // we don't fetch other pins if they won't be visible anyway
    if (mapViewState.showOther && !hideResults) {
      const externalPinsPayload = { ...requestPayload };
      externalPinsPayload.$filters!.territories = {
        $nin: territoriesCondition.value,
      };
      const externalPinsResponse: ListResponse<MapEntry> = yield callApi(
        "fetchMapPins",
        orgId,
        externalPinsPayload
      );
      externalPinsData = externalPinsResponse.data;
    }

    yield put(
      fetchTerritoryPins.success({
        ...categorizeMapEntries(response.data, hideResults),
        externalEntries: categorizeMapEntries(externalPinsData, hideResults),
        pinsCount: response.total,
      })
    );
    const territoryId: Territory["id"] | undefined = yield select(getTerritoryId);
    if (territoryId !== undefined) {
      yield put(setTerritoryPinsBounds({ bounds: viewport.bounds, territoryId }));
    }

    // Also refresh records list, if not in create/edit mode
    const mode: TerritoryModeState["mode"] = yield select(getTerritoryMode);
    if (mode === "view") {
      yield put(fetchTerritoryRecords.request({}));
    }

    // Refresh status of clusters which are within lasso selection
    const pins: CategorizedMapEntries = yield select(getTerritoryEntries);
    yield put(updateLassoClusters(pins.clusters));
  } catch (error) {
    yield put(fetchTerritoryPins.failure(error));
    yield put(handleError({ error }));
  }
}

// Tells the chat which territory this fetch listed, so it never sends the previous one's records.
function* putTerritoryQuery(
  territoryId: Territory["id"] | undefined,
  records?: TerritoryRecordsQuery
) {
  if (territoryId !== undefined) {
    yield put(setTerritoryQuery(records ? { records, territoryId } : { territoryId }));
  }
}

export function* onFetchTerritoryRecords() {
  try {
    const territoryId: Territory["id"] | undefined = yield select(getTerritoryId);
    const mapViewState: MapViewState = yield select(getTerritoryMapViewState);
    const searchFilter = mapViewState.search?.trim();

    const selectedBusinessRule: BusinessRule | undefined = yield select(getSelectedBusinessRule);

    // If we send empty filter for some entity, backend will send all records of that entity type
    // So we need to make sure records match selected business rule entities, if there is a selected business rule
    const visibleEntities =
      selectedBusinessRule && !("universal" in selectedBusinessRule.filters)
        ? intersection(
            Object.keys(selectedBusinessRule.filters) as EntityTypesSupportedByMapsPage[],
            mapViewState.visibleEntities
          )
        : mapViewState.visibleEntities;

    if (!visibleEntities.length) {
      yield put(
        fetchTerritoryRecords.success({
          records: [],
          recordsCount: 0,
        })
      );
      yield* putTerritoryQuery(territoryId);
      return;
    }

    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const isLassoMode = activeTool === MapTool.LASSO;

    const selectedRecords: Set<string> = yield select(getSelectedRecords);
    const $offset = mapViewState.range.startRow;
    const $limit = mapViewState.range.endRow - mapViewState.range.startRow;

    const entityFilters: MapFilterModel = {};

    const orgId: Organization["id"] = yield select(getOrganizationId);

    if (selectedBusinessRule) {
      FILTER_ENTITY_TYPES.forEach((entityType) => {
        const businessRuleEntityFilter = selectedBusinessRule.filters?.[entityType];
        if (businessRuleEntityFilter && businessRuleEntityFilter.conditions.length > 0) {
          entityFilters[entityType] = createAndCondition([
            businessRuleEntityFilter,
            mapViewState.filter[entityType] ?? createEmptyFilterModel(),
          ]);
        } else if (mapViewState.filter[entityType]) {
          entityFilters[entityType] = mapViewState.filter[entityType];
        }
      });
    }

    const platformFilterModel = convertMapFilterToPlatformFilterModel(
      selectedBusinessRule ? entityFilters : mapViewState.filter,
      visibleEntities
    );

    if (searchFilter?.length) {
      platformFilterModel.name = { $in: searchFilter };
    }

    // The territories filter is only supported at the top level of $filters -> https://mapmycustomers.atlassian.net/browse/TART-25707
    const territoriesCondition = getSimpleConditionForField(
      mapViewState.filter.universal,
      UniversalFieldName.TERRITORIES
    );
    const filtersWithoutLasso: PlatformFilterModel = {
      ...platformFilterModel,
      ...(territoriesCondition && { territories: { $any: territoriesCondition.value } }),
    };

    if (isLassoMode && selectedRecords.size === 0) {
      // If we're in lasso, but haven't selected any records for whatever reason - it makes no sense
      // to request detailed list of records from backend
      yield put(
        fetchTerritoryRecords.success({
          records: [],
          recordsCount: 0,
        })
      );
      yield* putTerritoryQuery(territoryId);
      return;
    }

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    const pinLegendFilter = {
      pinLegends: MAP_ENTITY_TYPES.reduce(
        (result, entityType) => ({
          ...result,
          [entityType]: getColorShapeForEntity(
            mapViewState,
            colorPinLegends,
            shapePinLegends,
            entityType
          ),
        }),
        { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
      ),
    };

    const requestPayload = {
      $filters: {
        ...(isLassoMode
          ? withLassoIds(filtersWithoutLasso, selectedRecords, mapViewState.visibleEntities)
          : filtersWithoutLasso),
        ...pinLegendFilter,
        cadence: true,
        includeAccessStatus: true,
        includeAssociations: true,
        includeCustomFields: true,
        includeEmailSubscriptionStatus: true,
      },
      $limit,
      $offset,
      $order: convertToPlatformSortModel(mapViewState.sort),
    };

    const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, requestPayload);
    const savedFilters: SavedFilter[] | undefined = yield select(getSavedFilters, EntityType.PIN);
    // The bar names no area here, so a geoPoint condition reads "filtered".
    const isFiltered = isMapFilterApplied(mapViewState.filter, {
      implicitFields: [UniversalFieldName.TERRITORIES],
      visibleTypes: visibleEntities,
    });
    // Snapshot first, so the bar never pairs the new records with the previous query.
    yield* putTerritoryQuery(territoryId, {
      entityTypes: visibleEntities,
      filters: toContextFilters(filtersWithoutLasso),
      isFiltered: isFiltered || !!searchFilter,
      ruleName: selectedBusinessRule?.name,
      // The search and the territory itself are not the saved filter's, so they don't keep its name.
      savedFilterName: isFiltered
        ? getSavedFilterName(savedFilters, mapViewState.selectedSavedFilterId)
        : undefined,
      sort: toContextSort(mapViewState.sort),
      totalCount: isLassoMode ? undefined : response.total,
    });
    yield put(
      fetchTerritoryRecords.success({
        records: response.data,
        recordsCount: response.total,
      })
    );
  } catch (error) {
    yield put(fetchTerritoryRecords.failure(error));
    yield put(handleError({ error }));
    // A failed fetch must not leave its predecessor's query in the bar.
    const territoryId: Territory["id"] | undefined = yield select(getTerritoryId);
    yield* putTerritoryQuery(territoryId);
  }
}

export function* onUpdateTerritoryBusinessRules({
  payload,
}: ReturnType<typeof updateTerritoryBusinessRules.request>) {
  try {
    const { businessRules, territoryId } = payload;
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const currentBusinessRules: BusinessRule[] = yield select(getTerritoryBusinessRules);
    const currentRulesMap = new Map(currentBusinessRules.map((rule) => [rule.id, rule]));

    const newBusinessRules: OnlyRequiredFields<BusinessRule, "filters" | "name" | "territories">[] =
      [];
    const incomingExistingRules: BusinessRule[] = [];
    const incomingIds = new Set<number>();

    for (const rule of businessRules) {
      if (rule.id === undefined) {
        newBusinessRules.push(rule);
      } else {
        incomingExistingRules.push(rule as BusinessRule);
        incomingIds.add(rule.id);
      }
    }

    const deletedBusinessRuleIds = currentBusinessRules
      .filter((rule) => !incomingIds.has(rule.id))
      .map(({ id }) => id);

    const updatedBusinessRules: BusinessRule[] = [];
    const updatedBusinessRuleAssignments: BusinessRuleAssignmentPayload[] = [];

    for (const incoming of incomingExistingRules) {
      const current = currentRulesMap.get(incoming.id!);

      if (current && hasRuleChanged(current, incoming)) {
        updatedBusinessRules.push(incoming);
      }

      if (current && hasAssignmentChanged(current, incoming)) {
        const incomingUserIds = getTargetUserIds(incoming, territoryId);
        const currentUserIds = getTargetUserIds(current, territoryId);

        const currentSet = new Set(currentUserIds);
        const incomingSet = new Set(incomingUserIds);

        const assignment: BusinessRuleAssignmentPayload = {
          addedUserIds: incomingUserIds.filter((id) => !currentSet.has(id)),
          removedUserIds: currentUserIds.filter((id) => !incomingSet.has(id)),
          ruleId: current.id,
          territoryId,
        };

        if (assignment.addedUserIds.length || assignment.removedUserIds.length) {
          updatedBusinessRuleAssignments.push(assignment);
        }
      }
    }

    if (deletedBusinessRuleIds.length) {
      yield callApi("bulkDeleteTerritoryBusinessRules", orgId, deletedBusinessRuleIds);
    }

    if (updatedBusinessRules.length) {
      yield callApi(
        "bulkUpdateTerritoryBusinessRules",
        orgId,
        updatedBusinessRules.map((ruleSet) => ({
          ...ruleSet,
          filters: convertMapFilterToPlatformFilterModel(
            ruleSet.filters,
            "universal" in ruleSet.filters
              ? MAP_ENTITY_TYPES
              : (Object.keys(ruleSet.filters) as EntityTypesSupportedByMapsPage[])
          ),
          territoryIds: ruleSet.territories.map(({ territoryId }) => territoryId),
        }))
      );
    }

    if (newBusinessRules.length) {
      const createdBusinessRules: PlatformBusinessRule[] = yield callApi(
        "bulkCreateTerritoryBusinessRules",
        orgId,
        newBusinessRules.map((ruleSet) => ({
          ...ruleSet,
          filters: convertMapFilterToPlatformFilterModel(
            ruleSet.filters,
            "universal" in ruleSet.filters
              ? MAP_ENTITY_TYPES
              : (Object.keys(ruleSet.filters) as EntityTypesSupportedByMapsPage[])
          ),
          territoryIds: [territoryId],
        }))
      );

      const assignments: BusinessRuleAssignmentPayload[] = createdBusinessRules
        .map((rule, index) => ({
          addedUserIds: newBusinessRules[index].territories?.[0]?.userIds || [],
          removedUserIds: [], // no users removed at creation
          ruleId: rule.id,
          territoryId: territoryId,
        }))
        .filter(({ addedUserIds }) => addedUserIds.length > 0);

      if (assignments.length || updatedBusinessRuleAssignments.length) {
        yield callApi("bulkUpsertTerritoryBusinessRulesAssignments", orgId, [
          ...assignments,
          ...updatedBusinessRuleAssignments,
        ]);
      }
    } else {
      if (updatedBusinessRuleAssignments.length) {
        yield callApi(
          "bulkUpsertTerritoryBusinessRulesAssignments",
          orgId,
          updatedBusinessRuleAssignments
        );
      }
    }

    const territoryBusinessRules: ListResponse<PlatformBusinessRule> = yield callApi(
      "fetchTerritoryBusinessRules",
      orgId,
      {
        $filters: {
          territoryIds: [territoryId],
        },
        $order: "isSystemGenerated",
      }
    );

    const regularBusinessRules: BusinessRule[] = territoryBusinessRules.data.map((rule) => ({
      ...rule,
      filters: convertMapPlatformFilterToMapFilterModel(rule.filters),
    }));

    yield put(updateTerritoryBusinessRules.success(regularBusinessRules));
  } catch (error) {
    yield put(updateTerritoryBusinessRules.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateTerritory({ payload }: ReturnType<typeof updateTerritory.request>) {
  try {
    const { businessRules, onSuccess, shape, territory, userIdsToShareWith } = payload;
    yield put(updateTerritoryGlobal.request({ onSuccess, shape, territory, userIdsToShareWith }));

    const result: {
      failure?: ActionType<typeof updateTerritoryGlobal.failure>;
      success?: ActionType<typeof updateTerritoryGlobal.success>;
    } = yield race({
      failure: take(updateTerritoryGlobal.failure),
      success: take(updateTerritoryGlobal.success),
    });

    if (result.failure) {
      yield put(updateTerritory.failure());
      return;
    }

    if (businessRules?.length) {
      yield put(
        updateTerritoryBusinessRules.request({
          businessRules,
          territoryId: territory.id,
        })
      );
    }

    yield put(updateTerritory.success(result.success!.payload.territory));
  } catch (error) {
    yield put(updateTerritory.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateTerritorySharing({
  payload,
}: ReturnType<typeof updateTerritorySharing.request>) {
  try {
    const { onSuccess, territory, userIdsToShareWith } = payload;

    yield put(updateTerritorySharingGlobal.request({ onSuccess, territory, userIdsToShareWith }));

    const result: {
      failure?: ActionType<typeof updateTerritorySharingGlobal.failure>;
      success?: ActionType<typeof updateTerritorySharingGlobal.success>;
    } = yield race({
      failure: take(updateTerritorySharingGlobal.failure),
      success: take(updateTerritorySharingGlobal.success),
    });

    if (result.failure) {
      yield put(updateTerritorySharing.failure());
      return;
    }

    yield put(updateTerritorySharing.success(result.success!.payload.territory));
  } catch (error) {
    yield put(updateTerritorySharing.failure());
    yield put(handleError({ error }));
  }
}

export function* onCountPinsInTerritory({
  payload,
}: ReturnType<typeof countPinsInTerritory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const mapViewState: MapViewState = yield select(getTerritoryMapViewState);

    let baseFilter: PlatformListRequest["$filters"] = {};

    if (Array.isArray(payload)) {
      const polygon = [...(payload as LongLat[][])]; // clone since we're editing it below
      if (!polygon.length || polygon[0].length < 4) {
        yield put(countPinsInTerritory.success({ count: 0, total: 0 }));
        return;
      }

      // Close path if not closed already. Note: we only expect a single path here
      if (!isClosedPath(polygon[0])) {
        polygon[0] = [...polygon[0], polygon[0][0]];
      }

      baseFilter = { multipolygon: polygon };
    } else {
      if (!payload.regionIds.length) {
        yield put(countPinsInTerritory.success({ count: 0, total: 0 }));
        return;
      }

      const parsedAreaType = parseAreaType(payload.areaType);
      if (!parsedAreaType) {
        yield put(countPinsInTerritory.failure());
        return;
      }
      const { countryCode, level } = parsedAreaType;
      const fieldToFilterBy =
        level === TerritoryLevel.ADM1
          ? "geoAddress.regionCode"
          : level === TerritoryLevel.ADM2
            ? "fipsCode"
            : "geoAddress.postalCode";

      baseFilter = {
        $and: [
          {
            [fieldToFilterBy]: { $in: payload.regionIds },
            "geoAddress.countryCode": countryCode,
          },
        ],
      };
    }

    const hasVisibleEntities = mapViewState.visibleEntities.length > 0;

    const visibleRequest = hasVisibleEntities
      ? callApi("fetchMapPins", orgId, {
          $filters: toCountOnlyPinsFilters({
            ...baseFilter,
            entities: mapViewState.visibleEntities.reduce(
              (result, entityType) => ({
                ...result,
                [entityType]: {},
              }),
              {}
            ),
          }),
          $limit: 0,
        })
      : null;

    const totalRequest = callApi("fetchMapPins", orgId, {
      $filters: toCountOnlyPinsFilters({
        ...baseFilter,
        entities: MAP_ENTITY_TYPES.reduce(
          (result, entityType) => ({
            ...result,
            [entityType]: {},
          }),
          {}
        ),
      }),
      $limit: 0,
    });

    const [visibleResponse, totalResponse]: [ListResponse<MapEntry>, ListResponse<MapEntry>] =
      yield all([visibleRequest, totalRequest]);

    yield put(
      countPinsInTerritory.success({
        count: visibleResponse ? visibleResponse.total : 0,
        total: totalResponse.total,
      })
    );
  } catch (error) {
    yield put(countPinsInTerritory.failure());
    yield put(handleError({ error }));
  }
}

export function* onEnterTerritoryLassoMode() {
  try {
    const mode: MapMode = yield select(getMapMode);
    if (mode === MapMode.TERRITORIES) {
      yield put(
        fetchTerritoryRecords.success({
          records: [],
          recordsCount: 0,
        })
      );
      // The list is empty until a selection, so drop the non-lasso query and its count.
      const territoryId: Territory["id"] | undefined = yield select(getTerritoryId);
      yield* putTerritoryQuery(territoryId);
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onExitTerritoryLassoMode() {
  try {
    yield put(setHighlight(new Set()));
    yield put(cleanLassoPaths());
    yield put(selectMapTool.request(undefined));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchTerritoryLassoSelection({
  payload,
}: ReturnType<typeof fetchTerritoryLassoSelection.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const mapViewState: MapViewState = yield select(getTerritoryMapViewState);

    const activeTool: MapTool | undefined = yield select(getMapViewTool);
    const lassoPaths: Array<GeoPath> = yield select(getMapLassoPaths);

    const isLassoMode = activeTool === MapTool.LASSO && lassoPaths.length > 0;

    if (isLassoMode) {
      const entityFilters: MapFilterModel = {
        universal: createEmptyFilterModel(),
        ...mapViewState.filter,
      };

      const multipolygon: LongLat[][] = payload.request.lasso
        ? payload.request.lasso
        : lassoPaths.map((path) =>
            path.map((item: google.maps.LatLng): LongLat => [item.lng(), item.lat()])
          );

      const mapFilter = convertMapFilterToPlatformFilterModel(
        entityFilters,
        mapViewState.visibleEntities
      );

      const requestPayload = {
        $filters: {
          ...mapFilter,
          cadence: true,
          includeAccessStatus: true,
          multipolygon,
          skipAccessibleCount: true,
        },
        $limit: 10000,
        $offset: 0,
        $order: convertToPlatformSortModel(mapViewState.sort),
      };

      const response: MapRecordsResponse = yield callApi("fetchMapPins", orgId, requestPayload);

      const excludedRecords: Set<string> = yield select(getExcludedRecords);
      const includedRecords: Set<string> = yield select(getIncludedRecords);

      const responseItems = response.data ?? [];
      const itemsWithoutExcluded = new Set(
        responseItems
          .map((entity) => mapEntityIdGetter(entity))
          .filter((value) => !excludedRecords.has(value))
      );
      const itemsWithIncluded = new Set([
        ...Array.from(includedRecords),
        ...Array.from(itemsWithoutExcluded),
      ]);
      yield put(setSelection(itemsWithIncluded));

      const selectionEntities = responseItems.filter(
        (entity) => !excludedRecords.has(mapEntityIdGetter(entity))
      );
      yield put(setSelectionEntities(selectionEntities));

      yield put(fetchTerritoryLassoSelection.success({ records: response.data }));
    } else {
      yield put(clearSelection());
      yield put(fetchTerritoryLassoSelection.success({ records: [] }));
    }

    yield put(resetRecordsListPagination());
    yield put(fetchTerritoryRecords.request({}));
  } catch (error) {
    yield put(fetchTerritoryLassoSelection.failure({ error }));
    yield put(handleError({ error }));
  }
}

export function* onDataView() {
  yield put(updateDataViewEntityTypeCount.request());
}

export function* onUpdateEntityTypeCount() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: MapViewState = yield select(getTerritoryMapViewState);
    const dataViewVisible: boolean = yield select(isTerritoryDataViewVisible);
    const mode: string = yield select(getTerritoryMode);
    const isViewMode = mode === "view";
    const territoryId: Territory["id"] | undefined = yield select(getTerritoryId);
    const data: LongLat[][] | SmartTerritoryConfig | undefined =
      yield select(getTerritoryDataViewData);
    if (!dataViewVisible || !data) {
      yield put(
        updateDataViewEntityTypeCount.success({
          entityTypeCount: {},
        })
      );
      return;
    }

    let $filters = {};

    if (isViewMode) {
      if (!territoryId) {
        yield put(updateDataViewEntityTypeCount.failure());
        return;
      }
      $filters = {
        territories: { $any: [territoryId] },
      };
    } else {
      if (Array.isArray(data)) {
        const polygon = [...(data as LongLat[][])]; // clone since we're editing it below
        if (!polygon.length || polygon[0].length < 4) {
          yield put(updateDataViewEntityTypeCount.success({ entityTypeCount: {} }));
          return;
        }
        polygon[0] = [...polygon[0], polygon[0][0]];
        $filters = { multipolygon: polygon };
      } else {
        const parsedAreaType = parseAreaType((data as SmartTerritoryConfig).areaType);
        if (!parsedAreaType) {
          yield put(updateDataViewEntityTypeCount.failure());
          return;
        }
        const { countryCode, level } = parsedAreaType;
        const fieldToFilterBy =
          level === TerritoryLevel.ADM1
            ? "geoAddress.regionCode"
            : level === TerritoryLevel.ADM2
              ? "fipsCode"
              : "geoAddress.postalCode";

        $filters = {
          $and: [
            {
              [fieldToFilterBy]: { $in: (data as SmartTerritoryConfig).regionIds },
              "geoAddress.countryCode": countryCode,
            },
          ],
        };
      }
    }

    const selectedBusinessRule: BusinessRule | undefined = yield select(getSelectedBusinessRule);

    const visibleEntities =
      selectedBusinessRule && !("universal" in selectedBusinessRule.filters)
        ? intersection(
            Object.keys(selectedBusinessRule.filters) as EntityTypesSupportedByMapsPage[],
            viewState.visibleEntities
          )
        : viewState.visibleEntities;

    const entityFilters: MapFilterModel = {};

    if (selectedBusinessRule) {
      FILTER_ENTITY_TYPES.forEach((entityType) => {
        const businessRuleEntityFilter = selectedBusinessRule.filters?.[entityType];
        if (businessRuleEntityFilter && businessRuleEntityFilter.conditions.length > 0) {
          entityFilters[entityType] = createAndCondition([
            businessRuleEntityFilter,
            viewState.filter[entityType] ?? createEmptyFilterModel(),
          ]);
        } else if (viewState.filter[entityType]) {
          entityFilters[entityType] = viewState.filter[entityType];
        }
      });
    }

    const requestPayloads = [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL].map((type) => {
      return {
        payload: {
          $filters: toCountOnlyPinsFilters({
            ...$filters,
            ...convertMapFilterToPlatformFilterModel(
              selectedBusinessRule ? entityFilters : viewState.filter,
              [type as EntityTypesSupportedByMapsPage]
            ),
          }),
          $limit: 0,
        },
        type,
      };
    });

    const [companyResponse, peopleResponse, dealResponse]: ListResponseTotal[] = yield all(
      requestPayloads.map(({ payload, type }) =>
        visibleEntities.includes(type as EntityTypesSupportedByMapsPage)
          ? callApi("fetchMapPins", orgId, payload)
          : { total: 0 }
      )
    );

    yield put(
      updateDataViewEntityTypeCount.success({
        entityTypeCount: {
          [EntityType.COMPANY]: companyResponse.total,
          [EntityType.DEAL]: dealResponse.total,
          [EntityType.PERSON]: peopleResponse.total,
        },
      })
    );
  } catch (error) {
    yield put(updateDataViewEntityTypeCount.failure());
    yield put(handleError({ error }));
  }
}

export function* onDownloadTerritoryRecords({
  payload,
}: ReturnType<typeof downloadTerritoryRecords>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentUser: User = yield select(getCurrentUser);

    const viewport: MapViewportState = yield select(getMapViewport);
    const mapViewState: MapViewState = yield select(getTerritoryMapViewState);
    const searchFilter = mapViewState.search?.trim();

    const features: OrganizationMetaData["features"] = yield select(getFeatures);
    const disallowColor = features?.[OrganizationFeature.DISALLOW_COLOR]?.enabled;

    const visibleEntityTypes = mapViewState.visibleEntities;

    const mapFilters = convertMapFilterToPlatformFilterModel(
      mapViewState.filter,
      visibleEntityTypes
    );

    const entities = mapFilters.entities as Record<
      EntityTypesSupportedByMapsPage,
      PlatformFilterModel
    >;
    Object.keys(entities).forEach((entityKey) => {
      entities[entityKey as EntityTypesSupportedByMapsPage].includeGroups = true;
    });

    if (searchFilter?.length) {
      // Inject filtering by name in records list
      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
          if (!("$and" in entities[entityKey])) {
            entities[entityKey].$and = [];
          }
          const andCondition = entities[entityKey].$and;
          if (Array.isArray(andCondition)) {
            andCondition.push({ name: { $in: searchFilter } });
          }
        });
      }
    }

    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    const bounds = viewport.bounds;
    const pinLegendFilter = disallowColor
      ? {
          pinLegends: MAP_ENTITY_TYPES.reduce(
            (result, entityType) => ({
              ...result,
              [entityType]: getColorShapeForEntity(
                mapViewState,
                colorPinLegends,
                shapePinLegends,
                entityType
              ),
            }),
            { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
          ),
        }
      : {};

    const intl = i18nService.getIntl();
    let entitiesWithResponseLimitCount = 0;
    let downloadedEntities = 0;
    for (const entityType of visibleEntityTypes) {
      const requiredEntity =
        mapFilters.entities &&
        (
          mapFilters.entities as Partial<
            Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>
          >
        )[entityType];
      const checkFilters = { ...mapFilters, entities: { [entityType]: requiredEntity } };
      const requestPayload = {
        $filters: {
          ...checkFilters,
          ...pinLegendFilter,
          bounds,
          cadence: true,
          includeCustomFields: true,
        },
        $limit: 0,
      };
      const columns =
        payload
          .get(entityType)
          ?.filter(({ visible }) => visible)
          .map(({ field }) => field) ?? [];
      const response: MapRecordsResponse = yield callApi("fetchPins", orgId, requestPayload);
      if (response.total > MAX_ITEMS_TO_DOWNLOAD_FILE) {
        const $filters = convertToPlatformFilterModel(
          convertMapFilterModelToFilterModelForEntity(entityType, mapViewState.filter),
          getFieldModelByEntityType(entityType).fields.map((field) => ({ field, visible: true })),
          getFieldModelByEntityType(entityType)
        );
        const selectedColumns = columns.map(({ exportColumnName }) => exportColumnName);
        const payload = {
          description: "",
          name: `${
            intl
              ? getEntityTypeDisplayName(intl, entityType, {
                  lowercase: false,
                  plural: false,
                })
              : entityType
          } ${formatDate(new Date(), "Pp")}`,
          selectedColumns,
          selectedFilters: {
            ...$filters,
            ...pinLegendFilter,
            bounds,
            cadence: true,
            includeCustomFields: true,
          },
          tableName: entityType,
        };

        try {
          const report: Report = yield callApi("createReport", orgId, payload);
          yield callApi("generateReport", orgId, report.id, currentUser.username);
          entitiesWithResponseLimitCount += 1;
        } catch {
          notification.error({
            description: getErrorNotificationDescription(intl, payload.tableName),
            message: intl?.formatMessage(messages.error),
          });
        }
      } else {
        const response: MapRecordsResponse = yield callApi("fetchPins", orgId, {
          ...requestPayload,
          $limit: MAX_ITEMS_TO_DOWNLOAD_FILE,
        });
        let reportEntityType;
        switch (entityType) {
          case EntityType.COMPANY:
            reportEntityType = ReportType.COMPANIES;
            break;
          case EntityType.DEAL:
            reportEntityType = ReportType.DEALS;
            break;
          case EntityType.PERSON:
            reportEntityType = ReportType.PEOPLE;
            break;
        }
        if (response.data.length > 0) {
          downloadEntitiesAsCsv(
            `${reportEntityType}_preview.csv`,
            entityType,
            response.data,
            columns
          );
          downloadedEntities += 1;
        }
      }
    }
    if (intl) {
      if (downloadedEntities > 0) {
        notification.success({
          message: intl.formatMessage(messages.success, {
            multiple: downloadedEntities > 1,
          }),
        });
      }
      if (entitiesWithResponseLimitCount > 0) {
        notification.warning({
          description: intl.formatMessage(messages.dataFileTooLargeDescription),
          message: intl.formatMessage(messages.dataFileTooLarge, {
            multiple: entitiesWithResponseLimitCount > 1,
          }),
        });
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onCountPinsInTerritoryBusinessRule({
  payload: { filters, successCallback, territoryRegion },
}: ReturnType<typeof countPinsInTerritoryBusinessRule>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const platformFilterModel = convertMapFilterToPlatformFilterModel(
      filters,
      "universal" in filters
        ? MAP_ENTITY_TYPES
        : (Object.keys(filters) as EntityTypesSupportedByMapsPage[])
    );

    if (Array.isArray(territoryRegion)) {
      const polygon = [...(territoryRegion as LongLat[][])];
      if (!polygon.length || polygon[0].length < 4) {
        yield call(successCallback, 0);
        return;
      }

      if (!isClosedPath(polygon[0])) {
        polygon[0] = [...polygon[0], polygon[0][0]];
      }

      const requestPayload = {
        $filters: toCountOnlyPinsFilters({
          ...platformFilterModel,
          multipolygon: polygon,
        }),
        $limit: 0,
      };

      const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, requestPayload);
      yield call(successCallback, response.total);
    } else {
      if (!territoryRegion.regionIds.length) {
        return;
      }

      const parsedAreaType = parseAreaType(territoryRegion.areaType);
      if (!parsedAreaType) {
        return;
      }
      const { countryCode, level } = parsedAreaType;
      const fieldToFilterBy =
        level === TerritoryLevel.ADM1
          ? "geoAddress.regionCode"
          : level === TerritoryLevel.ADM2
            ? "fipsCode"
            : "geoAddress.postalCode";

      const requestPayload = {
        $filters: toCountOnlyPinsFilters({
          ...platformFilterModel,
          $and: [
            ...(platformFilterModel.$and ?? []),
            {
              [fieldToFilterBy]: { $in: territoryRegion.regionIds },
              "geoAddress.countryCode": countryCode,
            },
          ],
        }),
        $limit: 0,
      };
      const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, requestPayload);
      yield call(successCallback, response.total);
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchDataForTerritoryMetrics({
  payload: { businessRule, entityType, legend, successCallback, territoryRegion },
}: ReturnType<typeof fetchDataForTerritoryMetrics>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const fieldModel = getFieldModelByEntityType(entityType);

    const legendFilters = convertFromPlatformFilterModel(
      legend.filters?.$filters ?? {},
      fieldModel
    );

    const ruleSetFilters = businessRule?.filters;

    const entityFilters: MapFilterModel = {};

    if (ruleSetFilters && ruleSetFilters[entityType]) {
      entityFilters[entityType] = createAndCondition([ruleSetFilters[entityType], legendFilters]);
      entityFilters["universal"] = ruleSetFilters["universal"];
    } else {
      entityFilters[entityType] = legendFilters;
    }

    const platformFilterModel = convertMapFilterToPlatformFilterModel(entityFilters, [entityType]);

    const aggregationType = legend.thematic?.aggregation;
    const fieldName = legend.customField ? legend.customField.esKey : legend.fieldName;

    const $aggs = legend.default
      ? legend.filters?.$aggs
      : aggregationType &&
          fieldName &&
          aggregationType !== BoundaryLegendAggregationType.VALUE_COUNT
        ? {
            [aggregationType]: [fieldName],
          }
        : undefined;

    if (Array.isArray(territoryRegion)) {
      const polygon = [...(territoryRegion as LongLat[][])];
      if (!polygon.length || polygon[0].length < 4) {
        yield call(successCallback, 0);

        return;
      }

      if (!isClosedPath(polygon[0])) {
        polygon[0] = [...polygon[0], polygon[0][0]];
      }

      const requestPayload = {
        $aggs,
        $filters: {
          ...platformFilterModel,
          multipolygon: polygon,
        },
        $limit: 0,
      };

      if ($aggs) {
        const response: AggregatedListResponse<
          MapEntity,
          CountAggregationItem<MapEntity>
        > = yield callApi("fetchPins", orgId, requestPayload);
        yield call(
          successCallback,
          aggregationType !== BoundaryLegendAggregationType.VALUE_COUNT
            ? (response.aggregations[`${aggregationType}_${fieldName}`]?.value ?? 0)
            : response.total
        );
      } else {
        const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, {
          ...requestPayload,
          $filters: toCountOnlyPinsFilters(requestPayload.$filters),
        });
        yield call(successCallback, response.total);
      }
    } else {
      if (!territoryRegion.regionIds.length) {
        return;
      }

      const parsedAreaType = parseAreaType(territoryRegion.areaType);
      if (!parsedAreaType) {
        return;
      }
      const { countryCode, level } = parsedAreaType;
      const fieldToFilterBy =
        level === TerritoryLevel.ADM1
          ? "geoAddress.regionCode"
          : level === TerritoryLevel.ADM2
            ? "fipsCode"
            : "geoAddress.postalCode";

      const requestPayload = {
        $aggs,
        $filters: {
          ...platformFilterModel,
          $and: [
            ...(platformFilterModel.$and ?? []),
            {
              [fieldToFilterBy]: { $in: territoryRegion.regionIds },
              "geoAddress.countryCode": countryCode,
            },
          ],
        },
        $limit: 0,
      };
      if ($aggs) {
        const response: AggregatedListResponse<
          MapEntity,
          CountAggregationItem<MapEntity>
        > = yield callApi("fetchPins", orgId, requestPayload);
        yield call(
          successCallback,
          aggregationType !== BoundaryLegendAggregationType.VALUE_COUNT
            ? (response.aggregations[`${aggregationType}_${fieldName}`]?.value ?? 0)
            : response.total
        );
      } else {
        const response: ListResponse<MapEntry> = yield callApi("fetchMapPins", orgId, {
          ...requestPayload,
          $filters: toCountOnlyPinsFilters(requestPayload.$filters),
        });
        yield call(successCallback, response.total);
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchTerritoryBusinessRulesRecords({
  payload: { combineOperator, failureCallback, filters, query, successCallback, territoryRegion },
}: ReturnType<typeof fetchTerritoryBusinessRulesRecords.request>) {
  try {
    const platformFilterModel = convertMapFilterToPlatformFilterModel(
      filters,
      "universal" in filters
        ? MAP_ENTITY_TYPES
        : (Object.keys(filters) as EntityTypesSupportedByMapsPage[]),
      combineOperator
    );

    const orgId: Organization["id"] = yield select(getOrganizationId);

    const mapViewState: MapViewState = yield select(getMapViewSettings);
    const colorPinLegends: PinLegend[] = yield select(getAllColorLegends);
    const shapePinLegends: PinLegend[] = yield select(getAllShapeLegends);

    let pinLegendFilter: PlatformFilterModel = {};
    pinLegendFilter = {
      pinLegends: MAP_ENTITY_TYPES.reduce(
        (result, entityType) => ({
          ...result,
          [entityType]: getColorShapeForEntity(
            mapViewState,
            colorPinLegends,
            shapePinLegends,
            entityType
          ),
        }),
        { $offset: getLocalTimeZoneFormattedOffset() } as PinLegendsFilter
      ),
    };

    if (Array.isArray(territoryRegion)) {
      const polygon = [...(territoryRegion as LongLat[][])];
      if (!polygon.length || polygon[0].length < 4) {
        yield call(successCallback, [], 0);
        yield put(fetchTerritoryBusinessRulesRecords.success());
        return;
      }

      if (!isClosedPath(polygon[0])) {
        polygon[0] = [...polygon[0], polygon[0][0]];
      }

      if (query?.trim().length) {
        platformFilterModel.$and = [
          ...(platformFilterModel.$and ?? []),
          {
            name: { $in: query },
          },
        ];
      }

      const requestPayload = {
        $filters: {
          ...platformFilterModel,
          ...pinLegendFilter,
          multipolygon: polygon,
        },
        $limit: 100,
      };
      const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, requestPayload);
      yield call(successCallback, response.data, response.total);
      yield put(fetchTerritoryBusinessRulesRecords.success());
    } else {
      if (!territoryRegion.regionIds.length) {
        yield put(fetchTerritoryBusinessRulesRecords.success());
        return;
      }

      const parsedAreaType = parseAreaType(territoryRegion.areaType);
      if (!parsedAreaType) {
        yield put(fetchTerritoryBusinessRulesRecords.failure());
        return;
      }
      const { countryCode, level } = parsedAreaType;
      const fieldToFilterBy =
        level === TerritoryLevel.ADM1
          ? "geoAddress.regionCode"
          : level === TerritoryLevel.ADM2
            ? "fipsCode"
            : "geoAddress.postalCode";

      if (query?.trim().length) {
        platformFilterModel.$and = [
          ...(platformFilterModel.$and ?? []),
          {
            name: { $in: query },
          },
        ];
      }

      const requestPayload = {
        $filters: {
          ...platformFilterModel,
          ...pinLegendFilter,
          $and: [
            ...(platformFilterModel.$and ?? []),
            {
              [fieldToFilterBy]: { $in: territoryRegion.regionIds },
              "geoAddress.countryCode": countryCode,
            },
          ],
        },
        $limit: 100,
      };
      const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, requestPayload);
      yield call(successCallback, response.data, response.total);
      yield put(fetchTerritoryBusinessRulesRecords.success());
    }
  } catch (error) {
    yield put(fetchTerritoryBusinessRulesRecords.failure());
    yield put(handleError({ error }));
    yield call(failureCallback);
  }
}

export function* territoryModeSagas() {
  yield takeLatest(enterTerritoryMode, onEnterTerritoryMode);
  yield takeLatest(exitTerritoryMode.request, onExitTerritoryMode);
  yield takeLatest(initializeTerritoryMode.request, onInitializeTerritoryMode);
  yield takeLatest(
    (action: Action) =>
      isActionOf(fetchTerritoryPins.request)(action) && !action.payload.updateOnly,
    onFetchTerritoryPins
  );
  yield takeEvery(
    (action: Action) =>
      isActionOf(fetchTerritoryPins.request)(action) && !!action.payload.updateOnly,
    onFetchTerritoryPins
  );
  yield takeLatest(fetchTerritoryRecords.request, onFetchTerritoryRecords);
  yield takeLatest(updateTerritory.request, onUpdateTerritory);
  yield takeLatest(updateTerritorySharing.request, onUpdateTerritorySharing);
  yield takeLatest(countPinsInTerritory.request, onCountPinsInTerritory);
  yield takeLatest([toggleDataViewVisibility, setDataViewData], onDataView);
  yield takeLatest(updateDataViewEntityTypeCount.request, onUpdateEntityTypeCount);
  yield takeLatest(enterLassoMode, onEnterTerritoryLassoMode);
  yield takeLatest(fetchTerritoryLassoSelection.request, onFetchTerritoryLassoSelection);
  yield takeLatest(exitTerritoryLassoMode, onExitTerritoryLassoMode);
  yield takeLatest(downloadTerritoryRecords, onDownloadTerritoryRecords);
  yield takeLatest(updateTerritoryBusinessRules.request, onUpdateTerritoryBusinessRules);
  yield takeEvery(countPinsInTerritoryBusinessRule, onCountPinsInTerritoryBusinessRule);
  yield takeLatest(
    fetchTerritoryBusinessRulesRecords.request,
    onFetchTerritoryBusinessRulesRecords
  );
  yield takeEvery(fetchDataForTerritoryMetrics, onFetchDataForTerritoryMetrics);
}
